import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import centralStockAPI from '../../services/centralStockAPI';
import { stockCategoryAPI } from '../../services/api';

const AddCentralStockItem = ({ open, onClose, selectedItem, onSuccess }) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // ─── Fetch active stock categories when dialog opens ───────────────────
    useEffect(() => {
        if (!open) return;

        const fetchCategories = async () => {
            setCategoriesLoading(true);
            try {
                const res = await stockCategoryAPI.getAll({ isActive: true, limit: 100 });
                // handle both { data: { data: [...] } } and { data: [...] } shapes
                const list = res.data?.data ?? res.data ?? [];
                setCategories(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error('Failed to fetch stock categories:', err);
                toast.error(t('stockCategory.fetchError', 'Failed to load categories'));
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, [open, t]);

    const formik = useFormik({
        initialValues: {
            name: selectedItem?.name || '',
            quantity: selectedItem?.quantity ?? '',
            minimumThreshold: selectedItem?.minimumThreshold ?? '',
            unit: selectedItem?.unit || '',
            price: selectedItem?.price ?? '',
            // category is stored as an ObjectId — keep the _id string for the Select
            category: selectedItem?.category?._id
                ?? selectedItem?.category
                ?? '',
            validUntil: selectedItem?.validUntil
                ? new Date(selectedItem.validUntil).toISOString().split('T')[0]
                : '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            name: Yup.string().required('Name is required'),
            quantity: Yup.number()
                .typeError('Quantity must be a number')
                .min(0, 'Quantity must be 0 or greater')
                .required('Quantity is required'),
            minimumThreshold: Yup.number()
                .typeError('Threshold must be a number')
                .min(0, 'Threshold must be 0 or greater')
                .required('Threshold is required'),
            unit: Yup.string().required('Unit is required'),
            price: Yup.number()
                .typeError('Price must be a number')
                .min(0, 'Price must be 0 or greater')
                .nullable(),
            category: Yup.string().nullable(),
            validUntil: Yup.date().nullable(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                const payload = {
                    ...values,
                    quantity: Number(values.quantity),
                    minimumThreshold: Number(values.minimumThreshold),
                    price: values.price === '' ? null : Number(values.price),
                    category: values.category === '' ? null : values.category,
                    validUntil: values.validUntil === '' ? null : values.validUntil,
                };

                if (selectedItem) {
                    await centralStockAPI.update(selectedItem._id, payload);
                    toast.success(t('centralStock.updateSuccess', 'Item updated successfully'));
                } else {
                    await centralStockAPI.create(payload);
                    toast.success(t('centralStock.createSuccess', 'Item created successfully'));
                }

                onSuccess();
                onClose();
            } catch (error) {
                console.error('Save item error:', error.response);
                toast.error(error.response?.data?.message || t('centralStock.saveError', 'Failed to save item'));
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {selectedItem
                    ? t('centralStock.editTitle', 'Edit Item')
                    : t('centralStock.addTitle', 'Add Item')}
            </DialogTitle>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent>

                    {/* Name */}
                    <TextField
                        fullWidth margin="dense"
                        name="name"
                        label={t('centralStock.name', 'Name')}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />

                    {/* Category */}
                    <FormControl
                        fullWidth
                        margin="dense"
                        error={formik.touched.category && Boolean(formik.errors.category)}
                    >
                        <InputLabel>
                            {categoriesLoading
                                ? t('common.loading', 'Loading...')
                                : t('centralStock.category', 'Category')}
                        </InputLabel>
                        <Select
                            name="category"
                            value={formik.values.category}
                            onChange={formik.handleChange}
                            label={t('centralStock.category', 'Category')}
                            disabled={categoriesLoading}
                        >
                            {/* Allow clearing the category */}
                            <MenuItem value="">
                                <em>{t('common.none', 'None')}</em>
                            </MenuItem>
                            {categories.map(cat => (
                                <MenuItem key={cat._id} value={cat._id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.category && formik.errors.category && (
                            <FormHelperText>{formik.errors.category}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Quantity */}
                    <TextField
                        fullWidth margin="dense"
                        name="quantity"
                        label={t('centralStock.quantity', 'Quantity')}
                        type="number"
                        value={formik.values.quantity}
                        onChange={formik.handleChange}
                        error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                        helperText={formik.touched.quantity && formik.errors.quantity}
                    />

                    {/* Minimum Threshold */}
                    <TextField
                        fullWidth margin="dense"
                        name="minimumThreshold"
                        label={t('centralStock.threshold', 'Minimum Threshold')}
                        type="number"
                        value={formik.values.minimumThreshold}
                        onChange={formik.handleChange}
                        error={formik.touched.minimumThreshold && Boolean(formik.errors.minimumThreshold)}
                        helperText={formik.touched.minimumThreshold && formik.errors.minimumThreshold}
                    />

                    {/* Unit */}
                    <TextField
                        fullWidth margin="dense"
                        name="unit"
                        label={t('centralStock.unit', 'Unit')}
                        value={formik.values.unit}
                        onChange={formik.handleChange}
                        error={formik.touched.unit && Boolean(formik.errors.unit)}
                        helperText={formik.touched.unit && formik.errors.unit}
                    />

                    {/* Price */}
                    <TextField
                        fullWidth margin="dense"
                        name="price"
                        label={t('centralStock.price', 'Price')}
                        type="number"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        error={formik.touched.price && Boolean(formik.errors.price)}
                        helperText={formik.touched.price && formik.errors.price}
                    />

                    {/* Valid Until */}
                    <TextField
                        fullWidth margin="dense"
                        name="validUntil"
                        label={t('centralStock.validUntil', 'Valid Until')}
                        type="date"
                        value={formik.values.validUntil}
                        onChange={formik.handleChange}
                        InputLabelProps={{ shrink: true }}
                    />

                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting
                            ? <CircularProgress size={24} />
                            : t('common.save', 'Save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddCentralStockItem;